/**
 * Phase 1.7 — generic entity-add dialog.
 *
 * Wraps the shared shadcn Dialog + a declarative form. Each v2 module
 * page passes its endpoint and field config; the dialog handles state,
 * validation (required-only), POST, and refetch trigger.
 *
 * For modules whose v1 dialog is significantly more complex than this
 * pattern (Plantings with its planner-driven flow, Templates with
 * type-specific defaults), the v2 page should use a dedicated dialog
 * component instead.
 *
 * Image attachment (added 2026-05): pass `entityType` (e.g. 'vehicle',
 * 'animal', 'crop') and the dialog renders an ImageStager. After the
 * POST returns the new row, the staged file (if any) is uploaded and
 * linked to `linked_entity_id = <new id>`. An image upload failure is
 * surfaced as a warning toast but does NOT roll back the entity create
 * — the record exists, the user can re-attach from the detail page.
 */

import { useState, type ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ImageStager } from '@/components/ImageStager';
import { authFetch } from '@/lib/auth';
import { uploadAttachment } from '@/lib/attachments';
import { toast } from 'sonner';

export type FieldType = 'text' | 'number' | 'date' | 'textarea' | 'select';

export interface FieldDef {
  name: string;
  label: string;
  type?: FieldType;
  placeholder?: string;
  required?: boolean;
  options?: { value: string; label: string }[];
  half?: boolean;
  defaultValue?: string;
}

export interface AddEntityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  submitLabel?: string;
  endpoint: string;
  fields: FieldDef[];
  /**
   * Transform raw form values before POST. Use to coerce numbers,
   * apply trims, and add hidden fields like `status: 'active'`.
   */
  transform?: (values: Record<string, string>) => Record<string, unknown>;
  trigger?: ReactNode;
  onAdded?: () => void;
  /**
   * When set, the dialog renders an ImageStager. After the entity is
   * created, the staged file (if any) is uploaded as an attachment
   * linked to the new entity. Should match the singular entity type
   * used elsewhere in the app (e.g. 'vehicle', 'animal', 'crop',
   * 'pasture', 'tool', 'garden', 'implement', 'inventory_item').
   */
  entityType?: string;
}

export function AddEntityDialog({
  open,
  onOpenChange,
  title,
  submitLabel,
  endpoint,
  fields,
  transform,
  trigger,
  onAdded,
  entityType,
}: AddEntityDialogProps): JSX.Element {
  const initial: Record<string, string> = {};
  for (const f of fields) initial[f.name] = f.defaultValue ?? '';
  const [values, setValues] = useState<Record<string, string>>(initial);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<File | null>(null);

  const reset = (): void => {
    const fresh: Record<string, string> = {};
    for (const f of fields) fresh[f.name] = f.defaultValue ?? '';
    setValues(fresh);
    setImageFile(null);
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    for (const f of fields) {
      if (f.required && !values[f.name]?.trim()) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const trimmed: Record<string, string> = {};
      for (const [k, v] of Object.entries(values)) trimmed[k] = v.trim();
      const body = transform ? transform(trimmed) : trimmed;
      const created = await authFetch<{ id: string }>(endpoint, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      // Chained image upload — non-blocking. If the image upload fails
      // the entity is still created and the user can attach from the
      // detail page; we surface the failure as a warning toast so it's
      // not silent.
      if (entityType && imageFile && created?.id) {
        try {
          await uploadAttachment(imageFile, {
            linked_entity_type: entityType,
            linked_entity_id: created.id,
          });
        } catch (uploadErr) {
          toast.warning(
            `Saved, but image upload failed: ${(uploadErr as Error).message ?? 'unknown error'}`,
          );
        }
      }

      toast.success(`${title.replace(/^add\s+/i, '').replace(/^new\s+/i, '')} added`);
      reset();
      onOpenChange(false);
      onAdded?.();
    } catch (err) {
      toast.error((err as Error).message ?? 'Failed to add');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          {renderFields(fields, values, setValues)}
          {entityType ? (
            <div className="space-y-2">
              <Label>Image</Label>
              <ImageStager
                file={imageFile}
                onChange={setImageFile}
                disabled={submitting}
              />
            </div>
          ) : null}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
          >
            {submitting ? 'Adding...' : (submitLabel ?? title)}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function renderFields(
  fields: FieldDef[],
  values: Record<string, string>,
  setValues: React.Dispatch<React.SetStateAction<Record<string, string>>>,
): JSX.Element[] {
  // Pair adjacent half-width fields into 2-col rows.
  const out: JSX.Element[] = [];
  for (let i = 0; i < fields.length; i++) {
    const f = fields[i];
    if (f.half && fields[i + 1]?.half) {
      out.push(
        <div key={`${f.name}-row`} className="grid grid-cols-2 gap-3">
          {renderField(f, values, setValues)}
          {renderField(fields[i + 1], values, setValues)}
        </div>,
      );
      i += 1;
    } else {
      out.push(renderField(f, values, setValues));
    }
  }
  return out;
}

function renderField(
  f: FieldDef,
  values: Record<string, string>,
  setValues: React.Dispatch<React.SetStateAction<Record<string, string>>>,
): JSX.Element {
  const setVal = (next: string): void => setValues((v) => ({ ...v, [f.name]: next }));
  if (f.type === 'textarea') {
    return (
      <div key={f.name} className="space-y-2">
        <Label>{f.label}{f.required ? ' *' : ''}</Label>
        <Textarea
          rows={3}
          value={values[f.name] ?? ''}
          onChange={(e) => setVal(e.target.value)}
          placeholder={f.placeholder}
        />
      </div>
    );
  }
  if (f.type === 'select') {
    return (
      <div key={f.name} className="space-y-2">
        <Label>{f.label}{f.required ? ' *' : ''}</Label>
        <Select value={values[f.name] ?? ''} onValueChange={setVal}>
          <SelectTrigger>
            <SelectValue placeholder={f.placeholder ?? 'Select...'} />
          </SelectTrigger>
          <SelectContent>
            {(f.options ?? []).map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  return (
    <div key={f.name} className="space-y-2">
      <Label>{f.label}{f.required ? ' *' : ''}</Label>
      <Input
        type={f.type ?? 'text'}
        value={values[f.name] ?? ''}
        onChange={(e) => setVal(e.target.value)}
        placeholder={f.placeholder}
      />
    </div>
  );
}
